<template>
  <div class="checkout-step-five" :class="{ loading: !stripeElementReady && isPaymentModeDeposit }">
    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-12">
            <h3 class="checkout-step__headline">
              <span v-if="isPaymentModeDeposit">{{ $t('pages.checkoutStepFive.title') }}</span>
              <span v-if="isPaymentModeInvoice"> {{ $t('pages.checkoutStepFive.onInvoiceTitle') }} </span>
              <span v-if="isPaymentModeOnSite"> {{ $t('pages.checkoutStepFive.onSiteTitle') }} </span>
              <span v-if="isPaymentModeBankTransfer"> {{ $t('pages.checkoutStepFive.bankTransferTitle') }} </span>
              <span v-if="isPaymentModePayByLink"> {{ $t('pages.checkoutStepFive.payByLinkTitle') }} </span>
            </h3>
        </div>
        <div class="col-12 col-md-12">
          <span class="checkout-step__copy"
            v-html="
              $t('pages.checkoutStepFive.copyTotal', {
                total: getFormattedValue(price.total),
              })
            ">
          </span>
          <span v-if="isPaymentModeDeposit && !depositIsTotal"
            class="checkout-step__copy ml-1"
            v-html="
              $t('pages.checkoutStepFive.bankTransferDownPayment', {
                down_payment: downPaymentText,
              })
            "
          ></span>
          <div v-else>
            <p class="checkout-step__copy" v-if="isPaymentModeInvoice">
              {{ $t('pages.checkoutStepFive.payOnInvoice') }}
            </p>
            <p class="checkout-step__copy" v-if="isPaymentModeOnSite">
              {{ $t('pages.checkoutStepFive.payOnSite') }}
            </p>
            <p v-if="isPaymentModeBankTransfer">
              <span class="checkout-step__copy">
                {{ $t('pages.checkoutStepFive.bankTransferInfo') }}
              </span>
              <span class="checkout-step__copy" v-if="!depositIsTotal"
                v-html="
                  $t('pages.checkoutStepFive.bankTransferDownPayment', {
                  down_payment: downPaymentText
                })"></span>
              <span class="checkout-step__copy" v-else>
                {{ $t('pages.checkoutStepFive.bankTransferFullAmount') }}
              </span>
            </p>
            <p v-if="isPaymentModePayByLink">
              <span class="checkout-step__copy">
                {{ $t('pages.checkoutStepFive.payByLinkDescription') }}
              </span>
              <span class="checkout-step__copy" v-if="!depositIsTotal"
                v-html="
                  $t('pages.checkoutStepFive.payByLinkDownPayment', {
                  down_payment: downPaymentText
                })"></span>
              <span class="checkout-step__copy" v-else>
                {{ $t('pages.checkoutStepFive.payByLinkFullAmount') }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- currency disclaimer -->
    <div
      class="checkout__currency-warning"
      :class="{
        'checkout__currency-warning--highlight': selectedCurrencyIsNotDefault,
      }"
    >
      <div class="warning-title" v-if="selectedCurrencyIsNotDefault">
        <iconWarning class="icon"></iconWarning>
        <span
          v-html="$t('pages.checkoutStepFive.currencyDisclaimer.title')"
        ></span>
      </div>
      <div
        class="warning-content"
        v-html="
          $t('pages.checkoutStepFive.currencyDisclaimer.copy', {
            currencyName: getCurrency.name,
            targetUrl: faqUrl('11').href,
          })
        "
      ></div>
    </div>

    <!-- checkboxes -->
    <form class="checkout--form" v-if="!checkout.status.error" novalidate>
      <div class="form-check" :class="{ error: errors.has('toc') }">
        <input
          type="checkbox"
          class="form-check-input"
          id="toc"
          name="toc"
          v-model="toc"
          v-validate="{ required: true }"
        />
        <label class="form-check-label" for="toc" v-if="!isBVCD">
          <span
            v-if="!!urls.toc"
            v-html="
              $t('pages.checkoutStepFive.toc.hasCampgroundTerms', {
                terms_url_campground: urls.toc,
                terms_url_ci: $t('meta.terms_url.ci'),
                privacy_url_ci: $t('meta.privacy.url'),
              })
            "
          ></span>
          <span
            v-else
            v-html="
              $t('pages.checkoutStepFive.toc.hasNoCampgroundTerms', {
                terms_url_ci: $t('meta.terms_url.bvcd'),
                privacy_url_ci: $t('meta.privacy.url'),
              })
            "
          ></span>
        </label>
        <label class="form-check-label" for="toc" v-else>
          <span
            v-if="!!urls.toc"
            v-html="
              $t('pages.checkoutStepFive.tocBvcd.hasCampgroundTerms', {
                terms_url_campground: urls.toc,
                terms_url_bvcd: $t('meta.terms_url.bvcd'),
                privacy_url_bvcd: $t('meta.privacy.url'),
              })
            "
          ></span>
          <span
            v-else
            v-html="
              $t('pages.checkoutStepFive.tocBvcd.hasNoCampgroundTerms', {
                terms_url_bvcd: $t('meta.terms_url.bvcd'),
                privacy_url_bvcd: $t('meta.privacy.url'),
              })
            "
          ></span>
        </label>
      </div>
      <div
        class="form-check"
        :class="{ error: errors.has('privacy') }"
        v-if="urls.privacy_policy"
      >
        <input
          type="checkbox"
          class="form-check-input"
          id="privacy"
          name="privacy"
          v-model="privacy"
          v-validate="{ required: true }"
        />
        <label class="form-check-label" for="privacy">
          <span
            v-html="
              $t('pages.checkoutStepFive.privacy', {
                privacy_url: urls.privacy_policy,
              })
            "
          ></span>
        </label>
      </div>
    </form>

    <div id="turnstile-container"></div>

    <CiButton v-if="!isPaymentModeDeposit"
      class="navigation__button button--primary" @click="doConfirmPendingBooking()"
    >
      <template>
        <span>{{ $t('generic.buttons.bookNow') }}</span>
        <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
      </template>
    </CiButton>

    <!-- stripe payment -->
    <div v-if="isPaymentModeDeposit">
      <div id="payment-div" v-show="!checkout.status.error"></div>
      <CiButton v-if="stripeElementReady"
        class="navigation__button button--primary mt-4" 
        @click="submitStripePayment()"
        :disabled="isSubmitting"
      >
        <template>
          <span>{{ $t('pages.checkoutStepFive.payButton') }} {{  getFormattedValue(price.deposit_value) }}</span>
          <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
        </template>
      </CiButton>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { CiGenericNavigation, CiInfoBox, CiButton } from '@/components/index'

import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'
import { initStripe, createStripePaymentElement } from '@/utils/stripe'
import store from '@/store'
import { isStripeRedirect } from '@/utils/stripeRedirect'

export default {
  name: 'CheckoutStepFive',
  components: {
    CiGenericNavigation,
    CiInfoBox,
    iconArrowBlack,
    iconArrowWhite,
    iconWarning,
    CiButton,
  },
  data() {
    return {
      initPaymentResult: {},
      stripeElementObj: null,
      stripeElementReady: false,
      stripeInstance: null,
      privacy: false,
      toc: false,
      isBVCD:
        process.env.VUE_APP_BVCD !== '' &&
        process.env.VUE_APP_BVCD !== null &&
        process.env.VUE_APP_BVCD !== undefined,
      isSubmitting: false,
    }
  },
  created() {
    if (isStripeRedirect()) {
      this.setLoader(true)
      return
    }
    this.trackEcommerceEvents(5)
    if (this.bookingConfirmation && this.bookingConfirmation.payment) {
      if (this.bookingConfirmation.payment.success === false) {
        this.$nextTick(() => {
          this.showErrorDialog(
            this.$t('modals.CiPaymentError.title'),
            `${this.$t('modals.CiPaymentError.content')}
            ${this.$t('modals.CiPaymentError.errorGeneric')}`
          )
        })
      }
    }
  },
  mounted() {
    if (isStripeRedirect()) {
      this.$router.push({
        name: 'Confirmation',
        params: {
          ...this.$route.params,
          bookingId: this.checkout.responseCheckout.booking_id,
        },
      })
      return
    }

    this.initPayment()
    if (!this.isPaymentModeDeposit) {
      store.dispatch('turnstileStore/loadTurnstile')
      if(!this.turnstileToken) {
        store.dispatch('turnstileStore/renderTurnstile')
      }
    }
    else {
      store.dispatch('turnstileStore/unloadTurnstile')
    }

    if (!window.Stripe && this.isPaymentModeDeposit) {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.setAttribute('data-stripe-script', 'true')
      document.head.appendChild(script)

      this._stripeScript = script
    }
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      checkout: 'checkout',
      price: 'price',
      status: 'status',
      bookingConfirmation: 'confirmation',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      currencies: 'currencies',
      sessionTimeout: 'sessionTimeout',
      urls: 'urls',
    }),

    ...mapGetters('turnstileStore', {
      turnstileToken: 'turnstileToken',
    }),

    getCurrency() {
      return this.currencies.find(
        currency => currency.code === this.campground.currency
      )
    },

    selectedCurrencyIsNotDefault() {
      return this.campground.currency !== this.currency.code
    },

    depositIsTotal() {
      return this.price.deposit_value === this.price.total
    },

    depositIsPercent() {
      return (
        !this.depositIsTotal &&
        this.price.deposit_value !== this.price.minimum_fixed_deposit
      )
    },

    isPaymentModeDeposit() {
      return this.price.payment_mode === 'now'
    },

    isPaymentModeInvoice() {
      return this.price.payment_mode === 'invoice'
    },

    isPaymentModeOnSite() {
      return this.price.payment_mode === 'on_site'
    },

    isPaymentModeBankTransfer() {
      return this.price.payment_mode === 'bank_transfer'
    },

    isPaymentModePayByLink() {
      return this.price.payment_mode === 'pay_by_link'
    },

    downPaymentText() {
      if (this.depositIsPercent) {
        return `${this.getFormattedValue(this.price.deposit_value)} (${this.price.deposit_percent}%)`
      }
      if (this.depositIsTotal) {
        return this.getFormattedValue(this.price.total)
      }

      return this.getFormattedValue(this.price.deposit_value)
    },
  },
  methods: {
    ...mapActions('application', {
      resetStepStatus: 'resetStepStatus',
      setLoader: 'setLoader',
      setStepStatus: 'setStepStatus',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    ...mapActions('booking', {
      performCheckout: 'performCheckout',
      prepareBookingForPayment: 'prepareBookingForPayment',
      checkPaymentSession: 'checkPaymentSession',
      resetState: 'resetState',
      resetCheckout: 'resetCheckout',
      resetStatus: 'resetStatus',
      setBilling: 'setBilling',
      setCheckout: 'setCheckout',
      resetTimer: 'resetTimer',
      confirmPendingBooking: 'confirmPendingBooking',
    }),

    back() {
      /* reset status - keep checkout in case no data changes */
      this.resetStatus()

      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: this.activeStep - 1 },
      })
    },

    getFormattedValue(value) {
      return this.$options.filters.currency(
        value,
        this.campground.currency,
        this.language.code
      )
    },

    async initPayment() {
      this.setLoader(true)
      return this.performCheckout()
        .then(result => {
          this.initPaymentResult = result
          if (result.success) {
            this.initPaymentDialog(result.payment)
          } else {
            this.showStartOverDialog()
          }
        })
        .catch(() => {
          this.showStartOverDialog()
        })
    },

    async initPaymentDialog(data) {
      if (!this.isPaymentModeDeposit) {
        this.setLoader(false)
        return
      }

      this.stripeElementReady = false
      try {
        const publishableKey = data.publishableKey || data.publishable_key
        const clientSecret = data.clientSecret || data.client_secret
        const stripeAccountId =
          data.stripeAccountId || data.stripe_account_id || null

        if (!publishableKey || !clientSecret) {
          this.showStartOverDialog()
          return
        }
        this.stripeInstance = await initStripe(publishableKey, stripeAccountId)
        this.stripeElementObj = await createStripePaymentElement(
          this.stripeInstance,
          clientSecret,
          '#payment-div',
          () => {
            this.stripeElementReady = true
            this.setLoader(false)
          },
          null,
          null,
          (error) => {
            this.onStripeError(error)
          }
        )
      } catch (error) {
        this.showStartOverDialog()
      }
    },

    async submitStripePayment() {
      if (!this.stripeElementObj || this.isSubmitting) return

      this.isSubmitting = true

      this.resetStatus()

      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        const el =
          this.$el.querySelector('.form-check.error') ||
          this.$el.querySelector('.checkout--form')

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        this.isSubmitting = false
        return
      }
      this.setLoader(true)

      await this.$nextTick()
      await new Promise(r => requestAnimationFrame(r))

      let stripeResult

      try {
        stripeResult = await this.stripeElementObj.submit()
      } catch (e) {
        this.setLoader(false)
        this.onStripeError(e)
        this.isSubmitting = false
        return
      }

      if (stripeResult && stripeResult.error) {
        this.setLoader(false)
        this.onStripeError(stripeResult.error)
        this.isSubmitting = false
        return
      }

      if (stripeResult && stripeResult.success && stripeResult.paymentIntent) {
        this.isSubmitting = false
        this.$router.push({
          name: 'Confirmation',
          params: {
            ...this.$route.params,
            bookingId: this.checkout.responseCheckout.booking_id,
          },
        })
      }
    },

    onStripeError(error = {}) {
      if (error.code && error.code.startsWith('incomplete_')) {
        this.setLoader(false)
        return
      }

      if (error.code && error.code.startsWith('invalid_')) {
        this.setLoader(false)
        return
      }

      let message = ''

      if (error.message) {
        message = error.message
      } else if (error.error && error.error.message) {
        message = error.error.message
      } else {
        message = this.$t('modals.CiPaymentError.errorGeneric')
      }

      this.showErrorDialog(
        this.$t('modals.CiPaymentError.title'),
        `${this.$t('modals.CiPaymentError.content')} ${message}`
      )
    },

    reInitPaymentWithCheckoutResponse() {
      if (this.stripeElementObj) {
        this.stripeElementObj.update()
      }
    },

    reInitPayment() {
      this.setLoader(false)
      this.resetStatus()
      this.resetCheckout()
      if (this.stripeElementObj) {
        this.stripeElementObj.unmount()
      }
      this.initPayment()
    },

    onSessionExpired() {
      this.setCheckout({ key: 'responseStripe', value: {} })
      this.showErrorDialog(
        this.$t('modals.CiSessionExpired.title'),
        this.$t('modals.CiSessionExpired.contentPaymentRestart'),
        this.reInitPayment
      )
      this.setLoader(false)
    },

    showStartOverDialog() {
      let errorContent = this.$t('modals.CiCheckoutGenericError.content')
      if (
        this.checkout.responseCheckout.data &&
        this.checkout.responseCheckout.data.non_field_errors &&
        this.checkout.responseCheckout.data.non_field_errors.length
      ) {
        errorContent = this.checkout.responseCheckout.data.non_field_errors[0]
      }
      this.showErrorDialog(
        this.$t('modals.CiCheckoutGenericError.title'),
        errorContent,
        this.startOver
      )
      this.setLoader(false)
    },

    startOver() {
      /* go back to step 1 */
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: 1 },
      })

      /* reset booking state */
      this.resetState()

      /* reset step status */
      this.resetStepStatus()
    },

    showErrorDialog(
      title,
      content,
      proceedAction = () => {},
      proceedButtonText = null
    ) {
      this.$modal.show('dialog', {
        buttons: {
          primary: {
            active: true,
            classes: 'button--primary button--center',
            text: proceedButtonText,
          },
          secondary: { active: false },
        },
        content: content,
        proceedEvent: proceedAction, //this.reInitPayment,
        title: title,
      })
    },

    faqUrl(id) {
      return this.$router.resolve({
        name: 'FAQ',
        params: this.$route.params,
        query: { q: id },
      })
    },

    doConfirmPendingBooking() {
      /* validate checkboxes */
      this.$validator.validateAll().then(result => {
        if (result) {
          this.setLoader(true)
          return this.confirmPendingBooking({
            bookingId: this.checkout.responseCheckout.booking_id,
            token: this.turnstileToken,
          })
            .then(result => {
              if (result.success) {
                this.$router.push({
                  name: 'Confirmation',
                  params: {
                    ...this.$route.params,
                    bookingId: this.checkout.responseCheckout.booking_id,
                  },
                })
              } else {
                this.setLoader(false)
                this.showStartOverDialog()
              }
            })
            .catch(() => {
              this.setLoader(false)
              this.showStartOverDialog()
            })
        } else {
          /* block default functionality */
          let errors = []
          Object.values(this.$validator.errors.items).forEach(item => {
            if (item.field === 'toc') {
              errors.push('terms_checkbox')
            } else {
              errors.push(`${item.field}_checkbox`)
            }
          })
          this.$el
            .querySelector('.checkout--form')
            .scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
  },
  beforeDestroy() {
    if (this.stripeElementObj) {
      this.stripeElementObj.unmount()
    }
    if (this.turnstileToken) {
      store.dispatch('turnstileStore/clearTurnstileToken')
      store.dispatch('turnstileStore/resetTurnstile')
    }

    if (this._stripeScript && this._stripeScript.parentNode) {
      this._stripeScript.parentNode.removeChild(this._stripeScript)
      this._stripeScript = null
    }
  },
}
</script>

<style lang="scss" scoped>
.checkout-step-five.loading {
  opacity: 0.5;
}

.checkout-step-five__header {
  margin: 0 0 30px;
}

.checkout-step-five__headline {
  @include font-format(1.375rem, 1.625em);
}

.checkout-step-five__copy {
  margin: 0;

  &.checkout-step-five__copy--small {
    @include font-format(0.625em, 0.875em);
  }
}

.checkout--form {
  margin: 30px 0;
  .form-check.error {
    .form-check-input {
      outline: 2px solid #dc3545;
      outline-offset: 2px;
    }
    .form-check-label {
      color: #dc3545;
    }
  }

  .validation-error {
    margin-top: 6px;
    font-size: 0.875rem;
    color: #dc3545;
  }
}

.checkout__currency-warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  font-style: italic;
  background-color: transparent;
  border: 0 solid #d6d6d6;
  margin: 15px 0 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 0;
  }

  .warning-title + .warning-content {
    padding: 10px 25px 10px 60px;
  }

  &.checkout__currency-warning--highlight {
    border: 1px solid #f8e71c;
    background-color: #fefcdd;

    .warning-content {
      padding: 10px 25px 10px 25px;
    }
  }
}

#payment-div {
  margin: 20px 0 0 0;
  min-height: 200px;
}

.StripeElement--focus {
  border-color: $primary-green;
  box-shadow: 0 0 0 1px $primary-green;
}

.StripeElement--invalid {
  border-color: #dc3545;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}

.StripeElement .p-AccordionItem.p-AccordionItem--selected .p-AccordionButton {
  color: #000 !important;
}

#payment-div .p-AccordionItem.p-AccordionItem--selected .p-AccordionButton {
  color: #000 !important;
}
</style>
